/**
 * ADMIN MODULE — Dashboard
 * Tile launcher for internal ops. Two live tiles (LinkedIn Flywheel, Agents)
 * pull real counts; four are placeholders. If Paperclip is unreachable a
 * warning banner appears at the top.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getAgents } from "../utils/paperclipClient.js";
import { usePaperclipHealth } from "../hooks/usePaperclipHealth.js";
import AdminLayout from "../components/AdminLayout.jsx";

const ICONS = {
  flywheel: <svg viewBox="0 0 24 24"><path d="M4 19V5M4 19h16M8 19v-6M12 19v-9M16 19v-4" /></svg>,
  seo: <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>,
  agents: <svg viewBox="0 0 24 24"><rect x="5" y="8" width="14" height="11" rx="2" /><path d="M12 8V4M9 4h6M9 13h.01M15 13h.01M9.5 16h5" /></svg>,
  tickets: <svg viewBox="0 0 24 24"><path d="M4 5h16v5a2 2 0 0 0 0 4v5H4v-5a2 2 0 0 0 0-4z" /><path d="M14 5v14" /></svg>,
  nurture: <svg viewBox="0 0 24 24"><path d="M22 12c0 5.5-4.5 10-10 10S2 17.5 2 12 6.5 2 12 2a8 8 0 0 1 8 8c0 4-3 6-6 6a4 4 0 0 1-4-4c0-2.5 2-4 4-4" /></svg>,
  sales: <svg viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8M21 7v5M21 7h-5" /></svg>,
  finance: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5h4M9.5 14h4" /></svg>,
  lock: <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>,
};

function WarnBanner() {
  return (
    <div className="admin-banner" role="alert">
      <svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
      <span>Agents offline — Paperclip not reachable. Check your Tailscale connection.</span>
    </div>
  );
}

function LiveTile({ to, icon, title, sub, status, foot }) {
  return (
    <Link to={to} className="admin-tile live">
      {status}
      <div className="admin-tile-icon">{icon}</div>
      <div className="admin-tile-title">{title}</div>
      <div className="admin-tile-sub">{sub}</div>
      <div className="admin-tile-foot">{foot}</div>
    </Link>
  );
}

function SoonTile({ icon, title, sub }) {
  return (
    <div className="admin-tile soon" aria-disabled="true">
      <span className="admin-soon-badge">{ICONS.lock} Coming Soon</span>
      <div className="admin-tile-icon">{icon}</div>
      <div className="admin-tile-title">{title}</div>
      <div className="admin-tile-sub">{sub}</div>
    </div>
  );
}

async function count(table, build) {
  let q = supabase.from(table).select("*", { count: "exact", head: true });
  if (build) q = build(q);
  const { count: c, error } = await q;
  if (error) { console.error("[AdminDashboard.count]", table, error); return null; }
  return c ?? 0;
}

export default function AdminDashboard() {
  const health = usePaperclipHealth();
  const [fly, setFly] = useState(null);     // flywheel counts
  const [seo, setSeo] = useState(null);     // seo rank counts
  const [nurture, setNurture] = useState(null); // nurture counts
  const [agents, setAgents] = useState(null); // { total, online } | null

  useEffect(() => {
    let alive = true;
    (async () => {
      const [drafts, pending, warm, hot] = await Promise.all([
        count("linkedin_posts", (q) => q.eq("status", "draft")),
        count("comment_queue", (q) => q.eq("status", "pending")),
        count("linkedin_engagement", (q) => q.eq("is_warm_lead", true)),
        count("linkedin_engagement", (q) => q.eq("lead_status", "hot")),
      ]);
      if (alive) setFly({ drafts, pending, warm, hot });
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [keywords, articleDrafts, published] = await Promise.all([
        count("seo_keywords", (q) => q.in("status", ["new", "approved"])),
        count("seo_articles", (q) => q.eq("status", "draft")),
        count("seo_articles", (q) => q.eq("status", "published")),
      ]);
      if (alive) setSeo({ keywords, drafts: articleDrafts, published });
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [enrolled, active, sends] = await Promise.all([
        count("nurture_subscribers"),
        count("nurture_subscribers", (q) => q.eq("status", "active")),
        count("nurture_sends"),
      ]);
      if (alive) setNurture({ enrolled, active, sends });
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await getAgents();
      if (!alive) return;
      if (error || !Array.isArray(data)) { setAgents(null); return; }
      // "Up" = anything not offline/error/stopped (Paperclip's default is 'idle').
      const UP = new Set(["online", "idle", "ready", "active", "running", "busy", "working"]);
      const online = data.filter((a) => UP.has(a.status)).length;
      setAgents({ total: data.length, online });
    })();
    return () => { alive = false; };
  }, [health]);

  const n = (v) => (v == null ? "—" : v);
  const online = health === "online";

  return (
    <AdminLayout title="Operations" subtitle="Internal ops — staff only.">
      {health === "offline" && <WarnBanner />}

      <div className="admin-tile-grid">
        <LiveTile
          to="/admin/linkedin"
          icon={ICONS.flywheel}
          title="LinkedIn Flywheel"
          sub="Content engine, warm-lead pipeline, and comment approvals."
          foot={
            <>
              <span><b>{fly ? n(fly.drafts) : "…"}</b> drafts</span>
              <span><b>{fly ? n(fly.pending) : "…"}</b> pending</span>
              <span><b>{fly ? n(fly.warm) : "…"}</b> warm</span>
              <span><b>{fly ? n(fly.hot) : "…"}</b> hot</span>
            </>
          }
        />

        <LiveTile
          to="/admin/seo"
          icon={ICONS.seo}
          title="SEO Rank"
          sub="Keyword research, on-page audits, and the blog article engine."
          foot={
            <>
              <span><b>{seo ? n(seo.keywords) : "…"}</b> keywords</span>
              <span><b>{seo ? n(seo.drafts) : "…"}</b> drafts</span>
              <span><b>{seo ? n(seo.published) : "…"}</b> published</span>
            </>
          }
        />

        <LiveTile
          to="/admin/agents"
          icon={ICONS.agents}
          title="Agents"
          sub="Paperclip agent orchestration, runs, and issues."
          status={
            <span className="admin-tile-status">
              <span className={`admin-dot ${online ? "online" : health === "pending" ? "pending" : "offline"}`} />
            </span>
          }
          foot={
            <>
              <span><b>{agents ? agents.total : "—"}</b> agents</span>
              <span><b>{agents ? agents.online : "—"}</b> online</span>
              <span>{online ? "Connected" : health === "pending" ? "Checking…" : "Offline"}</span>
            </>
          }
        />

        <LiveTile
          to="/admin/nurture"
          icon={ICONS.nurture}
          title="Email Nurture"
          sub="Lead sequences, send automation, and funnel metrics."
          foot={
            <>
              <span><b>{nurture ? n(nurture.enrolled) : "…"}</b> enrolled</span>
              <span><b>{nurture ? n(nurture.active) : "…"}</b> active</span>
              <span><b>{nurture ? n(nurture.sends) : "…"}</b> sends</span>
            </>
          }
        />

        <LiveTile
          to="/admin/tickets"
          icon={ICONS.tickets}
          title="Support Tickets"
          sub="Cross-tenant support inbox — every ticket across tenants."
        />
        {/* Bug Tracker removed 2026-07-31 — Support Tickets already covers it,
            and two inboxes would just split where defects get reported. */}
        <SoonTile icon={ICONS.sales} title="Sales Pipeline" sub="Track deals from lead to closed-won." />
        <SoonTile icon={ICONS.finance} title="Finance" sub="Revenue, MRR, and billing health at a glance." />
      </div>
    </AdminLayout>
  );
}
